import React, { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, Copy, CircleCheck as CheckCircle2,
  TriangleAlert as AlertCircle, Phone, Hash, User
} from 'lucide-react';
import InputMask from 'react-input-mask-next';
import { useClientRatings } from '../hooks/useClientRatings';
import { ClientRatingBadge } from '../components/ClientRatingBadge';
import ClientDetailModal from '../components/ClientDetailModal';

interface Client {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  code: string;
  seller: {
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  contracts: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    payment_status: string;
    ruta: number;
    calculated_balance: number;
    installments_data: {
      id: string;
      due_date: string;
      amount: number;
      paid_amount: number | null;
      status: string;
    }[];
  }[];
  overall_status: 'active' | 'inactive';
}

export default function AllClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [cpfSearchTerm, setCpfSearchTerm] = useState('');
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const calculateOutstandingBalance = (installments: { amount: number; paid_amount: number | null }[]): number => {
    return installments.reduce((total, installment) => {
      const unpaid = installment.amount - (installment.paid_amount || 0);
      return total + unpaid;
    }, 0);
  };

  const fetchClients = async () => {
    if (!user) return;

    // Validate that at least one search term is provided
    if (!nameSearchTerm.trim() && !cpfSearchTerm.trim() && !phoneSearchTerm.trim()) {
      setError('Por favor, digite pelo menos um filtro (nome, CPF ou telefone) para buscar clientes.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          code,
          seller:seller_id(email),
          loan_amount,
          total_amount,
          created_at,
          payment_status,
          ruta,
          installments_data:installments(id, due_date, amount, paid_amount, status)
        `)
        .eq('unit_id', user?.unit_id)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(200);

      // Apply filters
      if (nameSearchTerm.trim()) {
        query = query.ilike('client_name', `%${nameSearchTerm.trim()}%`);
      }

      if (cpfSearchTerm.trim()) {
        const cleanCpf = cpfSearchTerm.replace(/\D/g, '');
        query = query.ilike('cpf', `%${cleanCpf}%`);
      }

      if (phoneSearchTerm.trim()) {
        const cleanPhone = phoneSearchTerm.replace(/\D/g, '');
        query = query.ilike('phone', `%${cleanPhone}%`);
      }

      const { data: contracts, error: contractsError } = await query;

      if (contractsError) throw contractsError;

      // Group contracts by client (using CPF as unique identifier)
      const clientsMap = new Map<string, Client>();

      contracts?.forEach(contract => {
        const existingClient = clientsMap.get(contract.cpf);

        if (existingClient) {
          const calculated_balance = calculateOutstandingBalance(contract.installments_data);
          existingClient.contracts.push({
            id: contract.id,
            loan_amount: contract.loan_amount,
            total_amount: contract.total_amount,
            created_at: contract.created_at,
            payment_status: contract.payment_status,
            ruta: contract.ruta,
            calculated_balance,
            installments_data: contract.installments_data
          });
        } else {
          const calculated_balance = calculateOutstandingBalance(contract.installments_data);
          clientsMap.set(contract.cpf, {
            id: contract.id,
            client_name: contract.client_name,
            cpf: contract.cpf,
            phone: contract.phone,
            relative_phones: contract.relative_phones,
            email: contract.email,
            address: contract.address,
            business_address: contract.business_address,
            pix_key: contract.pix_key,
            code: contract.code,
            seller: contract.seller,
            contract_files: [],
            overall_status: 'inactive',
            contracts: [{
              id: contract.id,
              loan_amount: contract.loan_amount,
              total_amount: contract.total_amount,
              created_at: contract.created_at,
              payment_status: contract.payment_status,
              ruta: contract.ruta,
              calculated_balance,
              installments_data: contract.installments_data
            }]
          });
        }
      });

      // Post-processing to determine overall_status for each client
      clientsMap.forEach((client, cpf) => {
        const hasAnyPending = client.contracts.some(contract =>
          contract.installments_data.some(
            (installment: any) => installment.status !== 'paid' || (installment.paid_amount || 0) < installment.amount
          )
        );
        client.overall_status = hasAnyPending ? 'active' : 'inactive';
      });

      const clientsList = Array.from(clientsMap.values());
      setClients(clientsList);
      setSearched(true);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };

  const handleNewContract = (client: Client) => {
    navigate('/dashboard/loan-simulation', {
      state: { prefillCpf: client.cpf }
    });
  };

  const handleCopyClientData = (client: Client) => {
    const clientData = `
Dados do Cliente:
Nome: ${client.client_name}
CPF: ${client.cpf}
Telefone: ${client.phone}

Telefones dos Parentes:
${client.relative_phones.map(phone => `${phone.relacao}: ${phone.numero}`).join('\n')}

Endereço Residencial: ${client.address}
Endereço Comercial: ${client.business_address}

${client.contracts.map(contract => `
Valor do Empréstimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Data: ${new Date(contract.created_at).toLocaleDateString('pt-BR')}
Ruta: ${contract.ruta}`).join('\n')}`.trim();

    navigator.clipboard.writeText(clientData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const clientIds = useMemo(() =>
    clients.map(client => client.id),
    [clients]
  );

  const { ratings } = useClientRatings(clientIds);

  const getBalanceDisplay = (balance: number) => {
    if (balance < 0) return { label: `Quitado +${formatCurrency(Math.abs(balance))} juros`, cls: 'text-emerald-600' };
    if (balance === 0) return { label: 'Quitado', cls: 'text-emerald-600' };
    return { label: formatCurrency(balance), cls: 'text-red-600' };
  };

  const getInitials = (name: string) =>
    name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Todos os Clientes</h1>
            <p className="text-sm text-gray-400 mt-0.5">Pesquise clientes, consulte contratos e acompanhe saldos</p>
          </div>
          {searched && !loading && clients.length > 0 && (
            <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full self-start sm:self-auto">
              {clients.length} cliente{clients.length !== 1 ? 's' : ''} encontrado{clients.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* ── SEARCH FORM ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Search className="w-4 h-4 text-blue-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Buscar Clientes</h2>
          </div>

          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Nome do Cliente
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={nameSearchTerm}
                    onChange={(e) => setNameSearchTerm(e.target.value)}
                    placeholder="Digite o nome do cliente..."
                    className="w-full pl-9 pr-4 h-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  CPF
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <InputMask
                    mask="999.999.999-99"
                    value={cpfSearchTerm}
                    onChange={(e) => setCpfSearchTerm(e.target.value)}
                    placeholder="000.000.000-00"
                    className="w-full pl-9 pr-4 h-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Telefone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={phoneSearchTerm}
                    onChange={(e) => setPhoneSearchTerm(e.target.value)}
                    placeholder="Digite o telefone..."
                    className="w-full pl-9 pr-4 h-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <button
                type="submit"
                disabled={loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim() && !phoneSearchTerm.trim())}
                className={`h-11 flex items-center justify-center gap-2 px-6 rounded-xl text-sm font-semibold transition-colors ${
                  loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim())
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Buscando...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Buscar Clientes</span>
                  </>
                )}
              </button>

              {(nameSearchTerm.trim() || cpfSearchTerm.trim()) && (
                <button
                  type="button"
                  onClick={() => {
                    setNameSearchTerm('');
                    setCpfSearchTerm('');
                    setPhoneSearchTerm('');
                    setClients([]);
                    setSearched(false);
                    setError(null);
                  }}
                  className="h-11 px-4 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors font-medium"
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* ── RESULTS ── */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Buscando clientes...</p>
          </div>
        ) : !searched ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Search className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold">Busque por clientes</p>
            <p className="text-sm text-gray-400 max-w-xs">
              Use os filtros acima para encontrar clientes pelo nome ou CPF. Digite pelo menos um dos campos.
            </p>
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold">Nenhum cliente encontrado</p>
            <p className="text-sm text-gray-400 max-w-xs">
              Nenhum cliente corresponde aos filtros aplicados. Tente ajustar os termos de busca.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div
                key={client.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Card header */}
                <div className="p-4 pb-3 border-b border-gray-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-white text-sm font-bold">{getInitials(client.client_name)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold text-gray-900 leading-tight truncate">{client.client_name}</p>
                        <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                          client.overall_status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {client.overall_status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {client.code && (
                          <span className="text-xs text-gray-400 font-mono">#{client.code}</span>
                        )}
                        <ClientRatingBadge rating={ratings.get(client.id) || null} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-gray-500 font-mono">{formatCPF(client.cpf)}</p>
                    {client.phone && (
                      <a
                        href={`https://wa.me/55${client.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        <Phone className="w-3 h-3" />
                        {formatPhone(client.phone)}
                      </a>
                    )}
                  </div>
                </div>

                {/* Contracts */}
                <div className="p-4 pb-3 flex-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">
                    Contratos ({client.contracts.length})
                  </p>
                  <div className="space-y-2">
                    {client.contracts.map((contract) => {
                      const bal = getBalanceDisplay(contract.calculated_balance);
                      return (
                        <div key={contract.id} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(contract.loan_amount)}</p>
                            <p className={`text-xs font-bold ${bal.cls}`}>{bal.label}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(contract.created_at).toLocaleDateString('pt-BR')} &bull; Rota {contract.ruta}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Contacts */}
                {client.relative_phones.filter(p => p.numero?.trim()).length > 0 && (
                  <div className="px-4 pb-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1.5">Contatos</p>
                    <div className="flex flex-wrap gap-1.5">
                      {client.relative_phones.filter(p => p.numero?.trim()).slice(0, 2).map((phone, index) => (
                        <a
                          key={index}
                          href={`https://wa.me/55${phone.numero.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-100"
                        >
                          <Phone className="w-3 h-3" />
                          {formatPhone(phone.numero)}
                          {phone.relacao && <span className="text-emerald-500 font-normal">({phone.relacao})</span>}
                        </a>
                      ))}
                      {client.relative_phones.filter(p => p.numero?.trim()).length > 2 && (
                        <span className="text-xs text-gray-400 px-2 py-0.5 bg-gray-100 rounded-lg">
                          +{client.relative_phones.filter(p => p.numero?.trim()).length - 2} mais
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="px-4 py-3 border-t border-gray-50 flex gap-2">
                  <button
                    onClick={() => setSelectedClient(client)}
                    aria-label="Ver detalhes do cliente"
                    className="flex-1 h-9 flex items-center justify-center gap-1.5 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Detalhes
                  </button>
                  <button
                    onClick={() => handleCopyClientData(client)}
                    aria-label="Copiar dados do cliente"
                    className={`flex-1 h-9 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-xl transition-colors ${
                      copied
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CLIENT DETAIL MODAL ── */}
      {selectedClient && (
        <ClientDetailModal
          contractId={selectedClient.contracts[0]?.id || selectedClient.id}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
}
